import React from "react";
import { StateSelect, TextField } from "components";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { usePropertyContext } from "../../context";
import formatDate from "utils/formatDate";
import { isValidDate } from "utils/validDate";
import ButtonV2 from "elements/ButtonV2";

const PreviousResidence = () => {
	const {
		flowId,
		next,
		previousAddressValue,
		setPreviousAddressValue,
		previousAddress2Value,
		setPreviousAddress2Value,
		previousCityValue,
		setPreviousCityValue,
		previousStateValue,
		setPreviousStateValue,
		previousZipcodeValue,
		setPreviousZipcodeValue,
		previousMoveOutDateValue,
		setPreviousMoveOutDateValue,
	} = useExemptionsContext();
	const { details } = usePropertyContext();
	const addressString = details?.address
		? `${details.address}${details.address2 ? ` ${details.address2}` : ""}, ${details.city}, ${details.state} ${details.zip_code}`
		: "";

	const trackEvent = useEventTracker();

	const handleSubmit = async () => {
		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_Previous_Residence_Answer,
			data: {
				"Previous Address":
					previousAddressValue +
					" " +
					(previousAddress2Value ? previousAddress2Value + ", " : "") +
					previousCityValue +
					", " +
					previousStateValue +
					" " +
					previousZipcodeValue,
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
			},
		});
		const trackingEventData = {
			"Previous Address":
				previousAddressValue +
				" " +
				(previousAddress2Value ? previousAddress2Value + ", " : "") +
				previousCityValue +
				", " +
				previousStateValue +
				" " +
				previousZipcodeValue,
		};
		next({ trackingEventData });
	};

	const buttonDisabled = React.useMemo(() => {
		if (["CALIFORNIA_EXEMPTION", "FLORIDA_EXEMPTION"].includes(flowId ?? "")) {
			return !(
				previousMoveOutDateValue &&
				previousAddressValue &&
				previousCityValue &&
				previousStateValue &&
				previousZipcodeValue
			);
		}

		return !(
			previousAddressValue &&
			previousCityValue &&
			previousStateValue &&
			previousZipcodeValue
		);
	}, [
		flowId,
		previousMoveOutDateValue,
		previousAddressValue,
		previousCityValue,
		previousStateValue,
		previousZipcodeValue,
	]);

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				<div className="property-profile-question-card-title-container">
					<p>Address of Previous Residence</p>
				</div>
				{flowId === "GEORGIA_EXEMPTION" && (
					<p className="exemption-question-card-subtitle sm">
						This helps us tell the county which address your previous homestead
						should be transferred from.
					</p>
				)}
				{flowId === "NEW_YORK_EXEMPTION" && (
					<p className="exemption-question-card-subtitle sm">
						This helps us tell the county which address your previous exemption
						or credit should be transferred from.
					</p>
				)}
				<TextField
					required
					label="Street Address"
					placeholder="Street Address"
					value={previousAddressValue ?? ""}
					containerClassName="mt-2"
					onChange={e => {
						setPreviousAddressValue(e.target.value);
					}}
				/>
				<TextField
					placeholder="Apt/Suite/Unit"
					value={previousAddress2Value ?? ""}
					containerClassName="appeal-address-address2"
					onChange={e => {
						setPreviousAddress2Value(e.target.value);
					}}
				/>
				<div className="appeal-address-city-state-zip">
					<TextField
						required
						label="City"
						placeholder="City"
						value={previousCityValue ?? ""}
						containerClassName="appeal-address-city"
						onChange={e => {
							setPreviousCityValue(e.target.value);
						}}
					/>
					<StateSelect
						required
						label="State"
						placeholder="State"
						value={previousStateValue ?? ""}
						containerClassName="appeal-address-state mt-0"
						onChange={e => {
							setPreviousStateValue(e.target.value);
						}}
					/>
					<TextField
						required
						numeric
						label="Zip Code"
						placeholder="Zip Code"
						value={previousZipcodeValue ?? ""}
						minLength={5}
						maxLength={5}
						pattern="\d{5,5}(-\d{4,4})?"
						containerClassName="appeal-address-zip"
						onChange={e => {
							setPreviousZipcodeValue(e.target.value);
						}}
					/>
					{["CALIFORNIA_EXEMPTION", "FLORIDA_EXEMPTION"].includes(
						flowId ?? ""
					) && (
						<TextField
							required
							label="Move out date"
							placeholder="MM/DD/YYYY"
							containerClassName="appeal-move-out-date"
							maxLength={10}
							minLength={10}
							value={previousMoveOutDateValue ?? ""}
							onChange={e => {
								const formatted = formatDate(e, previousMoveOutDateValue ?? "");
								const valid = isValidDate(formatted);

								if (valid) {
									setPreviousMoveOutDateValue(formatted);
								}
							}}
						/>
					)}
				</div>
			</div>
			{addressString && (
				<p className="sm exemptions-notification-message-azure mt-4 flex flex-column flex-gap-0_5">
					<u>Do not use {addressString} as your previous address.</u>
					Please be sure to use a real and true previous address as any
					discrepancy will cause your application to be rejected by the county.
					It’s okay to use an apartment complex address, family member residence
					or rented home as your previous address.
				</p>
			)}

			<ButtonV2 size="large" disabled={buttonDisabled} type="submit">
				Continue
			</ButtonV2>
		</form>
	);
};

export default PreviousResidence;
